// NEW_FILE_JUSTIFIED: RequestTabBody renders one tab's contents on the lead detail page — provider picker, status select, and read-only SectionedPolicyForm driven by the request's insurance type and payload values
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@convex/_generated/api';
import { SectionedPolicyForm } from '@/components/insurance/policy-application/SectionedPolicyForm';
import { RequestProviderPicker } from './RequestProviderPicker';
import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import type { IRequestTabBodyProps, TRequestStatus } from '@/@types/leadInsuranceRequest';
import type { IPolicyFieldValueV2 } from '@/@types/insurance';

const STATUSES: TRequestStatus[] = ['new', 'contacted', 'issued', 'lost'];
const STATUS_LABEL: Record<TRequestStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  issued: 'Issued',
  lost: 'Lost',
};

const NOOP_ON_CHANGE = (_key: string, _value: unknown): void => undefined;

export function RequestTabBody({ request }: IRequestTabBodyProps) {
  const updateStatus = useUpdateRequestStatus();

  const insuranceType = useQuery(api.insurance.types.getByIdWithSections, {
    typeId: request.insuranceTypeId,
  });

  const lead = useQuery(api.leads.get, { leadId: request.leadId });

  // Identity fields (name/email/phone/address) are stored on the lead document by
  // the website ingest, never in the request payload — overlay them so the type's
  // Customer Details section is not blank. Payload entries win when both exist.
  const identityValues: Record<string, unknown> = {
    name: lead?.name,
    email: lead?.email,
    phone: lead?.phoneNumber,
    address: lead?.address,
  };

  // Map payload entries to values Record<string, unknown> for SectionedPolicyForm
  const values: Record<string, unknown> = {
    ...identityValues,
    ...Object.fromEntries(request.payload.map(entry => [entry.fieldKey, entry.value])),
  };

  // Map payload entries to IPolicyFieldValueV2[] for archived field rendering
  const fieldValues: IPolicyFieldValueV2[] = request.payload.map(entry => ({
    fieldKey: entry.fieldKey,
    fieldLabel: entry.fieldLabel,
    value: typeof entry.value === 'string' ? entry.value : String(entry.value ?? ''),
  }));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Submission</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`provider-${request._id}`}>Provider</Label>
            <RequestProviderPicker
              orgId={request.orgId}
              requestId={request._id}
              currentProviderId={request.providerId ?? null}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`status-${request._id}`}>Status</Label>
            <Select
              value={request.status}
              onValueChange={async val => {
                await updateStatus(
                  request._id,
                  val as TRequestStatus // AS_JUSTIFIED: Select only emits values from STATUSES literal array; val is guaranteed to be TRequestStatus at runtime
                );
              }}
            >
              <SelectTrigger id={`status-${request._id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {insuranceType === undefined ? (
        <Skeleton className="h-72" />
      ) : insuranceType === null ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Insurance type was deleted. Original payload preserved below.
          </CardContent>
        </Card>
      ) : (
        <SectionedPolicyForm
          mode="readOnly"
          sections={insuranceType.sections}
          fieldValues={fieldValues}
          values={values}
          archivedFields={insuranceType.archivedFields}
          onChange={NOOP_ON_CHANGE}
        />
      )}
    </div>
  );
}
